// icon/index.ts — public surface for the icon bundle.
//
// Output is an IIFE (build-icon-bundle.mjs) loaded as a footer <script>, right after core-helpers.js
// (which defines iconMap + checkIfValidUUID and reads window.ICON_LOOKUP). It defines + self-runs
// window.ftMountIcons(root?), the placeholder scanner that retires PHP icon_map() rendering.
//
// Mount contract (mirrors ftMountThumbnails, includes/js-src/thumbnail/index.ts):
// PHP's icon_ph() (includes/functions/janus/core/icon_map.php) emits an INERT placeholder:
//   <i class="ft-icon" data-icon="<key>" data-w="16" data-h="16"
//      [data-pop="0"] [data-title="Custom"] [data-pic="https://cdn.../x.png"]
//      style="display:inline-block;width:16px;height:16px" aria-hidden="true"></i>
// The scanner reads the attrs, renders via the global iconMap() (or, for server-resolved catalog art
// not in ICON_LOOKUP, from data-pic), and replaces the <i> with the resulting <span><img> — idempotently.
//
// SINGLE-LABEL CONTRACT: the hydrated <span class="ft-icon-map" data-bs-original-title …> is the ONE
// label/tooltip source (mouseover) + press-hold target (data-ft-info-item-id on UUID keys). The <i>
// placeholder is aria-hidden and carries NO title/aria-label, so there is never a double label.
//
// AJAX/WS re-hydration is by EXPLICIT scoped re-invocation (ftMountIcons(scope)) wired next to the
// existing ftMountThumbnails calls in template.js — deliberately NOT a body-wide MutationObserver.

import { abilityTypeForKey } from '../shared/icon-type-badges';

declare global {
    interface Window {
        ftMountIcons?: (root?: Element | Document) => void;
    }
}

interface RuneIconHost {
    typeBadge(type: string, size: number): SVGElement;
    TYPE_LABEL?: Record<string, string>;
}

type IconWindow = Window & {
    RuneIcon?: RuneIconHost;
};

const FALLBACK_ICON = 'https://cdn.fatetide.com/images/icons/help-circle-outline.png';

// Escape a string for use inside a double-quoted HTML attribute (matches core-helpers.js iconMap()).
function escapeAttr(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

// Server-resolved catalog art (the icon_map() DB-fallback path: arbitrary item UUIDs whose bespoke
// picture is NOT in ICON_LOOKUP). The call site ships the pre-resolved URL as data-pic. We render the
// SAME single-label span iconMap() produces, just from the given URL instead of an ICON_LOOKUP key.
function buildFromPicUrl(pic: string, w: number, h: number, pop: boolean, title: string, key: string): string {
    const escaped = escapeAttr(title);
    const img = `<img src="${pic}" width="${w}" height="${h}" alt="${escaped}" loading="lazy" decoding="async"
                     onerror="this.src='${FALLBACK_ICON}'; this.onerror=null;">`;
    if (!pop) return img;
    const isUuid = typeof checkIfValidUUID === 'function' && checkIfValidUUID(key);
    const dataAttr = isUuid ? ` data-ft-info-item-id="${key}"` : '';
    return `<span class="d-inline-block ft-icon-map" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="${escaped}"${dataAttr}>${img}</span>`;
}

// Build the hydrated node for a rune type-badge: an <svg> (sized to the reserved w×h box, so zero
// layout shift), wrapped in the SAME single-label ft-icon-map span iconMap()/buildFromPicUrl() use so
// the tooltip contract is identical. `type` is the canonical item_type; the original placeholder key
// is never a UUID, so no press-hold data-ft-info-item-id.
function buildTypeBadge(RuneIcon: RuneIconHost, type: string, w: number, h: number, pop: boolean, title: string | null): Element {
    const svg = RuneIcon.typeBadge(type, Math.max(w, h)) as SVGElement;
    svg.setAttribute('width', String(w));
    svg.setAttribute('height', String(h));
    if (!pop) return svg;
    const label = (title && title.length) ? title : ((RuneIcon.TYPE_LABEL && RuneIcon.TYPE_LABEL[type]) || type);
    const span = document.createElement('span');
    span.className = 'd-inline-block ft-icon-map';
    span.setAttribute('data-bs-toggle', 'tooltip');
    span.setAttribute('data-bs-placement', 'top');
    span.setAttribute('data-bs-original-title', label);
    span.appendChild(svg);
    return span;
}

// Scan `root` (default document) for un-mounted icon placeholders and replace each with rendered icon HTML.
function ftMountIcons(root?: Element | Document): void {
    const scope: Element | Document = root || document;
    const mounts = scope.querySelectorAll('i.ft-icon:not([data-icon-mounted])');
    for (let i = 0; i < mounts.length; i++) {
        const el = mounts[i] as HTMLElement;
        el.setAttribute('data-icon-mounted', '1'); // mark first so a thrown render never loops on rescan
        try {
            const key = el.getAttribute('data-icon') || '';
            const w = Number(el.getAttribute('data-w')) || 25;
            const h = Number(el.getAttribute('data-h')) || 25;
            const pic = el.getAttribute('data-pic');           // server-resolved art (DB-fallback path)
            const pop = el.getAttribute('data-pop') !== '0';   // tooltip on unless explicitly disabled
            const title = el.getAttribute('data-title');       // custom popup override, else null

            // Ability-type chips → unified rune badge (shape+colour+AP) when the rune bundle is present.
            const badgeType = !pic ? abilityTypeForKey(key) : '';
            if (badgeType) {
                const RuneIcon = (window as IconWindow).RuneIcon;
                if (RuneIcon && typeof RuneIcon.typeBadge === 'function') {
                    el.replaceWith(buildTypeBadge(RuneIcon, badgeType, w, h, pop, title));
                    continue;
                }
                // The rune bundle loads LATER in the footer than this bundle, so it may not be defined on
                // an early pass. Leave this placeholder un-mounted so a later pass upgrades it: the rune
                // bundle calls ftMountIcons() the moment it executes, and there's a window-load safety net
                // below. Badge-only keys must never fall through to a PNG, even without that bundle.
                el.removeAttribute('data-icon-mounted');
                continue;
            }

            const html = pic
                ? buildFromPicUrl(pic, w, h, pop, title ?? '', key)
                : iconMap(key, w, h, pop ? (title ?? '') : false);
            el.outerHTML = html; // replaces the <i> placeholder entirely (drops data-icon-mounted with it)
        } catch (e) {
            console.warn('ftMountIcons: mount failed', e);
        }
    }
}

window.ftMountIcons = ftMountIcons;

// Render page-load placeholders as soon as the bundle executes (footer <script>, so every placeholder
// above it is parsed), and again on DOMContentLoaded for any that came after. Idempotent: a mounted
// placeholder is replaced outright, so the second pass is a no-op.
if (document.readyState !== 'loading') {
    ftMountIcons();
} else {
    document.addEventListener('DOMContentLoaded', () => ftMountIcons());
}
// Resolve any ability-type badge placeholders deferred while waiting for the rune bundle.
window.addEventListener('load', () => ftMountIcons());

export {};
